import { inject, Service } from '@angular/core';
import { Camera, MediaResult } from '@capacitor/camera';
import { ImageCompressionService } from '../image/image-compression.service';

export class CameraAccessError extends Error {
  constructor(readonly translationKey: string) {
    super(translationKey);
    this.name = 'CameraAccessError';
  }
}

const ALLOWED_IMAGE_MIME_TYPES = new Set<string>([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/bmp',
  'image/avif',
]);

@Service()
export class CameraService {
  private readonly imageCompression = inject(ImageCompressionService);

  async takePhotoAsync(): Promise<string | null> {
    try {
      const result = await Camera.takePhoto({ quality: 80, includeMetadata: true });
      const dataUrl = await this.toDataUrlAsync(result);
      return dataUrl ? this.imageCompression.compressDataUrlAsync(dataUrl) : null;
    } catch (error: unknown) {
      if (this.isUserCancellation(error)) return null;
      throw error;
    }
  }

  async ensureCameraReadyForScanningAsync(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new CameraAccessError('camera.errors.notAvailable');
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      stream.getTracks().forEach((track) => track.stop());
    } catch (error: unknown) {
      throw new CameraAccessError(this.resolveCameraErrorKey(error));
    }
  }

  private resolveCameraErrorKey(error: unknown): string {
    const name = error instanceof Error ? error.name : '';
    switch (name) {
      case 'NotAllowedError':
        return 'camera.errors.permissionDenied';
      case 'NotFoundError':
        return 'camera.errors.noCameraFound';
      case 'NotReadableError':
        return 'camera.errors.inUse';
      default:
        return 'camera.errors.notAvailable';
    }
  }

  private async toDataUrlAsync(result: MediaResult): Promise<string | null> {
    if (result.webPath) {
      const response = await fetch(result.webPath);
      const blob = await response.blob();
      this.assertValidImageType(blob.type);
      return this.readBlobAsDataUrlAsync(blob);
    }

    if (result.thumbnail) {
      const format = result.metadata?.format === 'png' ? 'png' : 'jpeg';
      this.assertValidImageType(`image/${format}`);
      return `data:image/${format};base64,${result.thumbnail}`;
    }

    return null;
  }

  private assertValidImageType(mimeType: string): void {
    if (!ALLOWED_IMAGE_MIME_TYPES.has(mimeType.toLowerCase())) {
      throw new CameraAccessError('camera.errors.invalidFileType');
    }
  }

  private readBlobAsDataUrlAsync(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error('FileReader failed to read blob'));
      reader.readAsDataURL(blob);
    });
  }

  private isUserCancellation(error: unknown): boolean {
    return error instanceof Error && error.message.toLowerCase().includes('cancel');
  }
}
